import React, { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ENTITY_TYPES, TYPE_ORDER } from "@/lib/entities";
import { numberedTitles } from "@/lib/chapters";
import ChoicesEditor from "@/components/ChoicesEditor";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import {
  Sparkles, Plus, LayoutGrid, List, MoreVertical, Trash2, Wand2, Loader2,
  ArrowRight, GripVertical, Target, Activity, CheckCircle2, PenSquare, ListChecks, GitBranch,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { label: "Planned", dot: "#9ca3af" },
  review: { label: "Drafting", dot: "#f59e0b" },
  complete: { label: "Done", dot: "#10b981" },
};
const DOT = { red: "#ef4444", amber: "#f59e0b", emerald: "#10b981", violet: "#8b5cf6", sky: "#0ea5e9", cyan: "#06b6d4", stone: "#a8a29e" };

function chapterEntities(chapter, entities) {
  const text = (chapter.content || "").toLowerCase() + " " + (chapter.summary || "").toLowerCase();
  return entities.filter((e) => text.includes(e.name.toLowerCase()));
}

// Local state + debounced commit so typing stays smooth (no network/re-render per keystroke).
// Also syncs when the source value changes externally (e.g. AI "apply acts").
function useDebouncedField(initial, commit, delay = 500) {
  const [val, setVal] = useState(initial);
  const committed = useRef(initial);
  useEffect(() => {
    if (val === committed.current) return;
    const t = setTimeout(() => { committed.current = val; commit(val); }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val]);
  useEffect(() => {
    if (initial !== committed.current) { committed.current = initial; setVal(initial); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);
  return [val, setVal];
}

export default function BlueprintBoard({
  project, chapters, entities, api, projectId,
  onUpdateProject, onUpdateChapter, onReorder, onAddChapter, onCreateSection, onDeleteChapter, onOpenChapter, onOpenBeats,
}) {
  const [mode, setMode] = useState("corkboard"); // corkboard | outline
  const [logline, setLogline] = useState(project.logline || "");
  const [dragId, setDragId] = useState(null);
  const [expand, setExpand] = useState(null); // {type:'acts'|'chapters', actId?, suggestions, loading}
  const [expanding, setExpanding] = useState(false);
  const acts = project.acts || [];
  const branching = project.mode === "branching";

  // Auto-grow the logline so the full text is always visible (no fixed 2-row clip).
  const loglineRef = useRef(null);
  useEffect(() => {
    const el = loglineRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  }, [logline]);

  const grouped = useMemo(() => {
    const byOrder = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const map = {};
    acts.forEach((a) => (map[a.id] = []));
    const unassigned = [];
    byOrder.forEach((c) => {
      if (c.act_id && map[c.act_id]) map[c.act_id].push(c);
      else unassigned.push(c);
    });
    return { map, unassigned };
  }, [chapters, acts]);

  const chapterTitles = useMemo(
    () => numberedTitles([...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
    [chapters]
  );

  const bookProgress = useMemo(() => {
    if (!chapters.length) return 0;
    const done = chapters.filter((c) => c.status === "complete").length;
    return Math.round((done / chapters.length) * 100);
  }, [chapters]);

  const saveLogline = () => { if (logline !== project.logline) onUpdateProject({ logline }); };

  const flatten = (mapOverride, unassignedOverride) => {
    const ids = [];
    acts.forEach((a) => (mapOverride[a.id] || []).forEach((c) => ids.push(c.id)));
    (unassignedOverride || []).forEach((c) => ids.push(c.id));
    return ids;
  };

  const moveChapter = (draggedId, targetActId, beforeId) => {
    const map = {}; acts.forEach((a) => (map[a.id] = [...(grouped.map[a.id] || [])]));
    let unassigned = [...grouped.unassigned];
    // remove dragged
    const removeFrom = (arr) => { const i = arr.findIndex((c) => c.id === draggedId); if (i >= 0) return arr.splice(i, 1)[0]; return null; };
    let dragged = null;
    acts.forEach((a) => { const r = removeFrom(map[a.id]); if (r) dragged = r; });
    if (!dragged) dragged = removeFrom(unassigned);
    if (!dragged) return;
    dragged = { ...dragged, act_id: targetActId };
    const dest = targetActId ? map[targetActId] : unassigned;
    if (beforeId) {
      const idx = dest.findIndex((c) => c.id === beforeId);
      dest.splice(idx >= 0 ? idx : dest.length, 0, dragged);
    } else dest.push(dragged);
    if (!targetActId) unassigned = dest;
    const ids = flatten(map, unassigned);
    onReorder(ids);
    onUpdateChapter(draggedId, { act_id: targetActId || dragged.act_id });
  };

  const runExpand = async (type, act) => {
    setExpanding(true);
    setExpand({ type, actId: act?.id, suggestions: [], loading: true });
    try {
      const payload = type === "acts"
        ? { mode: "acts", logline: logline || project.logline || "", count: 3 }
        : { mode: "chapters", logline: logline || project.logline || "", act_title: act.title, act_pitch: act.pitch || "", count: 4,
            all_acts: acts.map((a) => ({ title: a.title || "", pitch: a.pitch || "" })),
            existing_chapters: (chapters || []).map((c) => ({ title: c.title || "", summary: c.summary || "" })) };
      const { data } = await api.post(`/projects/${projectId}/blueprint/expand`, payload);
      setExpand({ type, actId: act?.id, suggestions: data.suggestions, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI planning failed");
      setExpand(null);
    } finally {
      setExpanding(false);
    }
  };

  const applyExpand = async () => {
    if (!expand) return;
    if (expand.type === "acts") {
      const newActs = expand.suggestions.map((s, i) => ({
        id: acts[i]?.id || crypto.randomUUID(),
        title: s.title, pitch: s.pitch,
      }));
      // keep any extra existing acts beyond suggestions
      for (let i = expand.suggestions.length; i < acts.length; i++) newActs.push(acts[i]);
      await onUpdateProject({ acts: newActs });
      toast.success("Acts drafted from your logline");
    } else {
      for (const s of expand.suggestions) {
        // eslint-disable-next-line no-await-in-loop
        await onAddChapter(expand.actId, { title: s.title, summary: s.summary });
      }
      toast.success(`${expand.suggestions.length} chapters added`);
    }
    setExpand(null);
  };

  const addAct = () => {
    const newActs = [...acts, { id: crypto.randomUUID(), title: `Act ${acts.length + 1}`, pitch: "" }];
    onUpdateProject({ acts: newActs });
  };
  const updateAct = (id, patch) => onUpdateProject({ acts: acts.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const deleteAct = (id) => onUpdateProject({ acts: acts.filter((a) => a.id !== id) });

  const columns = [...acts, ...(grouped.unassigned.length ? [{ id: null, title: "Unassigned", pitch: "", _u: true }] : [])];

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Header row */}
      <div className="px-4 sm:px-6 py-4 border-b border-border flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-primary" />
            <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Logline — your North Star</span>
          </div>
          <Textarea ref={loglineRef} value={logline} onChange={(e) => setLogline(e.target.value)} onBlur={saveLogline}
            data-testid="logline-input" rows={2}
            placeholder="In 1–3 sentences: what is your book about? What's the main conflict?"
            className="font-manuscript text-base resize-none overflow-hidden min-h-[3.5rem] bg-background/60 border-border" />
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40" data-testid="story-mode-toggle">
            <button onClick={() => onUpdateProject({ mode: "linear" })} data-testid="mode-linear"
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${!branching ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>Linear</button>
            <button onClick={() => onUpdateProject({ mode: "branching" })} data-testid="mode-branching"
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${branching ? "bg-background shadow-sm text-primary" : "text-muted-foreground"}`}>
              <GitBranch className="w-3.5 h-3.5" /> Branching</button>
          </div>
          <Button variant="outline" size="sm" onClick={() => runExpand("acts")} disabled={expanding}
            data-testid="expand-acts-button" className="gap-2">
            <Wand2 className="w-4 h-4" /> AI: Expand to acts
          </Button>
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
            <button onClick={() => setMode("corkboard")} data-testid="board-mode-corkboard"
              className={`p-1.5 rounded-md ${mode === "corkboard" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}><LayoutGrid className="w-4 h-4" /></button>
            <button onClick={() => setMode("outline")} data-testid="board-mode-outline"
              className={`p-1.5 rounded-md ${mode === "outline" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}><List className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 sm:px-6 py-2 border-b border-border flex items-center gap-3 text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">{bookProgress}% complete</span>
        <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden max-w-xs">
          <div className="h-full bg-primary transition-all" style={{ width: `${bookProgress}%` }} />
        </div>
        <span>{chapters.length} chapters · {acts.length} acts</span>
      </div>

      {/* Board */}
      <div className={`flex-1 overflow-auto p-4 sm:p-6 ${mode === "corkboard" ? "" : "max-w-4xl mx-auto w-full"}`}>
        <div className={mode === "corkboard" ? "flex gap-4 items-start min-h-full" : "space-y-6"}>
          {columns.map((act) => (
            <ActColumn key={act.id || "unassigned"} act={act} mode={mode}
              chapters={act._u ? grouped.unassigned : (grouped.map[act.id] || [])}
              chapterTitles={chapterTitles}
              entities={entities} dragId={dragId} setDragId={setDragId}
              branching={branching} allChapters={chapters} onCreateSection={onCreateSection}
              onMove={moveChapter} onUpdateAct={updateAct} onDeleteAct={deleteAct}
              onExpandChapters={() => runExpand("chapters", act)}
              onAddChapter={() => onAddChapter(act.id)} onUpdateChapter={onUpdateChapter}
              onDeleteChapter={onDeleteChapter} onOpenChapter={onOpenChapter} onOpenBeats={onOpenBeats} expanding={expanding} />
          ))}
          {mode === "corkboard" && (
            <button onClick={addAct} data-testid="add-act-button"
              className="shrink-0 w-80 rounded-xl border-2 border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-colors h-32 flex items-center justify-center gap-2">
              <Plus className="w-5 h-5" /> Add act
            </button>
          )}
        </div>
        {mode === "outline" && (
          <button onClick={addAct} data-testid="add-act-button-outline"
            className="mt-4 w-full rounded-xl border-2 border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-colors py-3 flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> Add act
          </button>
        )}
      </div>

      {/* AI expand dialog */}
      <Dialog open={!!expand} onOpenChange={(v) => !v && setExpand(null)}>
        <DialogContent className="bg-popover max-w-lg" data-testid="expand-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              {expand?.type === "acts" ? "Suggested acts" : "Suggested chapters"}
            </DialogTitle>
            <DialogDescription>
              {expand?.type === "acts"
                ? "AI broke your logline into a story structure. Apply to draft your acts."
                : "AI proposed chapters for this act. Apply to add them to the board."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2.5 py-1">
            {expand?.loading ? (
              <div className="flex flex-col items-center py-10 text-muted-foreground">
                <Loader2 className="w-7 h-7 animate-spin text-primary mb-3" />
                Thinking through your story…
              </div>
            ) : expand?.suggestions.map((s, i) => (
              <div key={i} className="rounded-lg border border-border p-3 bg-card">
                <div className="font-display font-bold text-lg">{s.title}</div>
                <p className="text-sm text-muted-foreground mt-0.5">{s.pitch || s.summary}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExpand(null)}>Cancel</Button>
            <Button onClick={applyExpand} disabled={expand?.loading || !expand?.suggestions?.length}
              data-testid="apply-expand-button" className="gap-2">
              <CheckCircle2 className="w-4 h-4" /> Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActColumn({
  act, mode, chapters, chapterTitles, entities, dragId, setDragId, onMove, onUpdateAct, onDeleteAct,
  onExpandChapters, onAddChapter, onUpdateChapter, onDeleteChapter, onOpenChapter, onOpenBeats, expanding,
  branching, allChapters, onCreateSection,
}) {
  const [over, setOver] = useState(false);
  const isUn = act._u;
  const cls = mode === "corkboard" ? "shrink-0 w-80" : "w-full";
  const [title, setTitle] = useDebouncedField(act.title, (v) => onUpdateAct(act.id, { title: v }));
  const [pitch, setPitch] = useDebouncedField(act.pitch || "", (v) => onUpdateAct(act.id, { pitch: v }));

  return (
    <div className={`${cls} rounded-xl border ${over ? "border-primary bg-primary/5" : "border-border bg-card/40"} transition-colors flex flex-col`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (dragId) onMove(dragId, act.id, null); setDragId(null); }}
      data-testid={`act-column-${act.id || "unassigned"}`}>
      <div className="p-3 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          {isUn ? (
            <div className="font-display text-lg font-bold text-muted-foreground">{act.title}</div>
          ) : (
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              onBlur={() => onUpdateAct(act.id, { title })}
              className="font-display text-lg font-bold bg-transparent outline-none w-full focus:text-primary"
              data-testid={`act-title-${act.id}`} />
          )}
          {!isUn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`act-menu-${act.id}`}><MoreVertical className="w-4 h-4" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem onClick={onExpandChapters} disabled={expanding} data-testid={`expand-chapters-${act.id}`}>
                  <Wand2 className="w-4 h-4 mr-2" /> AI: Suggest chapters
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDeleteAct(act.id)} className="text-destructive focus:text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete act
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {!isUn && (
          <AutoGrowTextarea value={pitch} onChange={(e) => setPitch(e.target.value)}
            onBlur={() => onUpdateAct(act.id, { pitch })}
            placeholder="A sentence or two: what happens in this act?"
            minRows={2} data-testid={`act-pitch-${act.id}`}
            className="mt-1.5 w-full text-xs text-muted-foreground bg-transparent outline-none focus:text-foreground" />
        )}
      </div>

      <div className="p-2.5 space-y-2.5 flex-1">
        {chapters.map((c) => (
          <ChapterCard key={c.id} chapter={c} displayTitle={chapterTitles?.[c.id]} entities={entities} mode={mode}
            branching={branching} allChapters={allChapters} onCreateSection={onCreateSection} onOpenChapter={onOpenChapter}
            onDragStart={() => setDragId(c.id)} onDragEnd={() => setDragId(null)}
            onDropBefore={() => { if (dragId && dragId !== c.id) onMove(dragId, act.id, c.id); setDragId(null); }}
            onUpdate={(patch) => onUpdateChapter(c.id, patch)} onDelete={() => onDeleteChapter(c)}
            onOpen={() => onOpenChapter(c.id)} onPlan={() => onOpenBeats(c.id)} />
        ))}
        {!isUn && (
          <button onClick={onAddChapter} data-testid={`add-chapter-to-act-${act.id}`}
            className="w-full rounded-lg border border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-colors py-2 text-sm flex items-center justify-center gap-1.5">
            <Plus className="w-4 h-4" /> Chapter
          </button>
        )}
      </div>
    </div>
  );
}

function ChapterCard({ chapter, displayTitle, entities, mode, onDragStart, onDragEnd, onDropBefore, onUpdate, onDelete, onOpen, onPlan, branching, allChapters, onCreateSection, onOpenChapter }) {
  const chips = chapterEntities(chapter, entities);
  const st = STATUS[chapter.status] || STATUS.draft;
  const choices = Array.isArray(chapter.choices) ? chapter.choices : [];
  const [choicesOpen, setChoicesOpen] = useState(false);
  const [tension, setTension] = useState(chapter.tension ?? 50);
  const [title, setTitle] = useDebouncedField(displayTitle ?? chapter.title, (v) => onUpdate({ title: v }));
  const [summary, setSummary] = useDebouncedField(chapter.summary || "", (v) => onUpdate({ summary: v }));

  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropBefore(); }}
      data-testid={`board-chapter-${chapter.id}`}
      className="group rounded-lg border border-border bg-card p-3 cursor-grab active:cursor-grabbing hover:border-primary/50 hover:shadow-sm transition-all">
      <div className="flex items-start gap-1.5">
        <GripVertical className="w-4 h-4 text-muted-foreground/40 mt-0.5 shrink-0" />
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          onClick={(e) => e.stopPropagation()} onBlur={() => onUpdate({ title })}
          className="font-semibold text-sm bg-transparent outline-none w-full focus:text-primary"
          data-testid={`board-chapter-title-${chapter.id}`} />
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <AutoGrowTextarea value={summary} onChange={(e) => setSummary(e.target.value)}
        onClick={(e) => e.stopPropagation()} onBlur={() => onUpdate({ summary })} minRows={2}
        placeholder="One-sentence summary of this chapter…"
        data-testid={`board-chapter-summary-${chapter.id}`}
        className="mt-1.5 w-full text-xs text-muted-foreground bg-transparent outline-none focus:text-foreground" />

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {chips.slice(0, 6).map((e) => (
            <span key={e.id} title={`${e.name} (${ENTITY_TYPES[e.type]?.label})`}
              className="w-2.5 h-2.5 rounded-full" style={{ background: DOT[ENTITY_TYPES[e.type]?.color] || "#9ca3af" }} />
          ))}
          {chips.length > 6 && <span className="text-[10px] text-muted-foreground">+{chips.length - 6}</span>}
        </div>
      )}

      <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            <Activity className="w-3 h-3" /> Tension
          </span>
          <span className="text-[10px] font-mono-code text-muted-foreground" data-testid={`board-tension-value-${chapter.id}`}>{tension}</span>
        </div>
        <Slider value={[tension]} min={0} max={100} step={5} className="w-full"
          onValueChange={(v) => setTension(v[0])} onValueCommit={(v) => onUpdate({ tension: v[0] })}
          data-testid={`board-tension-${chapter.id}`} />
      </div>

      {branching && (
        <button onClick={(e) => { e.stopPropagation(); setChoicesOpen(true); }} data-testid={`board-choices-btn-${chapter.id}`}
          className="mt-2.5 w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-1.5 text-[11px] text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors">
          <GitBranch className="w-3.5 h-3.5" />
          {choices.length ? `${choices.length} choice${choices.length !== 1 ? "s" : ""}` : "Add choices"}
        </button>
      )}

      <CardBeats chapter={chapter} onUpdate={onUpdate} onPlan={onPlan} />

      <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-border" onClick={(e) => e.stopPropagation()}>
        <Select value={chapter.status} onValueChange={(v) => onUpdate({ status: v })}>
          <SelectTrigger className="h-6 text-[11px] px-1.5 w-auto gap-1 border-none bg-transparent" data-testid={`board-status-${chapter.id}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: st.dot }} />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="draft">Planned</SelectItem>
            <SelectItem value="review">Drafting</SelectItem>
            <SelectItem value="complete">Done</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <button onClick={onOpen} data-testid={`open-chapter-${chapter.id}`}
            className="text-[11px] text-primary font-semibold flex items-center gap-1 hover:gap-1.5 transition-all">
            <PenSquare className="w-3.5 h-3.5" /> Write
          </button>
        </div>
      </div>

      {branching && (
        <Dialog open={choicesOpen} onOpenChange={setChoicesOpen}>
          <DialogContent className="bg-popover" data-testid={`choices-dialog-${chapter.id}`} onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-primary" /> Choices
              </DialogTitle>
              <DialogDescription>
                Link "{chapter.title}" to other sections. No choices = an ending (THE END).
              </DialogDescription>
            </DialogHeader>
            <ChoicesEditor chapter={chapter} chapters={allChapters} onUpdate={onUpdate}
              onCreateSection={onCreateSection} onOpenChapter={onOpenChapter} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


const beatUid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

// Compact beats list + quick-add on a Blueprint chapter card. Shares chapter.beats with
// the manuscript BeatPlanner, so beats added in either place show in both.
function CardBeats({ chapter, onUpdate, onPlan }) {
  const beats = Array.isArray(chapter.beats) ? chapter.beats : [];
  const [draft, setDraft] = useState("");
  const doneCount = beats.filter((b) => b.done).length;

  const commit = (list) => onUpdate({ beats: list });
  const addBeat = () => {
    const t = draft.trim();
    if (!t) return;
    commit([...beats, { id: beatUid(), text: t, done: false }]);
    setDraft("");
  };
  const toggle = (id) => commit(beats.map((b) => (b.id === id ? { ...b, done: !b.done } : b)));
  const remove = (id) => commit(beats.filter((b) => b.id !== id));

  return (
    <div className="mt-2.5 pt-2.5 border-t border-border" onClick={(e) => e.stopPropagation()} data-testid={`board-beats-${chapter.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          <ListChecks className="w-3 h-3" /> Beats {beats.length > 0 && <span className="font-mono-code">{doneCount}/{beats.length}</span>}
        </span>
        <button onClick={onPlan} data-testid={`board-beats-plan-${chapter.id}`}
          className="text-[10px] text-primary/80 hover:text-primary font-medium inline-flex items-center gap-0.5 transition-colors">
          <Wand2 className="w-3 h-3" /> Planner
        </button>
      </div>

      {beats.length > 0 && (
        <div className="space-y-1 mb-1.5">
          {beats.map((b, i) => (
            <div key={b.id} data-testid={`board-beat-${chapter.id}-${b.id}`}
              className="group/beat flex items-start gap-1.5 text-[11px] leading-snug">
              <span className="font-mono-code text-muted-foreground w-3.5 text-right shrink-0 pt-px">{i + 1}</span>
              <Checkbox checked={b.done} onCheckedChange={() => toggle(b.id)}
                data-testid={`board-beat-done-${chapter.id}-${b.id}`} className="mt-px shrink-0 w-3.5 h-3.5" />
              <span className={`flex-1 min-w-0 break-words ${b.done ? "line-through text-muted-foreground" : ""}`}>
                {b.text || <span className="italic text-muted-foreground">(empty beat)</span>}
              </span>
              <button onClick={() => remove(b.id)} data-testid={`board-beat-del-${chapter.id}-${b.id}`}
                className="opacity-0 group-hover/beat:opacity-100 text-muted-foreground hover:text-destructive shrink-0 transition-opacity">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBeat(); } }}
          placeholder="Add a beat…" data-testid={`board-beat-add-input-${chapter.id}`}
          className="h-7 text-[11px] px-2 bg-background/60" />
        <button onClick={addBeat} disabled={!draft.trim()} data-testid={`board-beat-add-btn-${chapter.id}`}
          className="shrink-0 w-7 h-7 rounded-md border border-border text-muted-foreground hover:text-primary hover:border-primary/50 disabled:opacity-40 grid place-items-center transition-colors">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
